import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  RefreshControl,
} from 'react-native';
import ReactDOM from 'react-dom';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Rect } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown, DepartmentPicker } from '../../components';
import { useTargetDepartment } from '../../hooks/useTargetDepartment';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { goalsApi, organizationsApi, kpisApi, organizationalUnitsApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { KPIType, Frequency, Polarity } from '../../types';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields, validateKpiMeasurementDate, validateKpiTargetConfigs } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  configFromKpi,
  buildKpiTargetPayload,
} from '../kpis/components/KpiAnnualTargetSection';
type RouteProps = RouteProp<MainStackParamList, 'AddGoal'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  goalWeight: string;
  startDate: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  hasReadings?: boolean;
  responsibleUnits: string[]; // Units responsible for executing this KPI (additive; goal-linked in edit mode)
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragHandleIcon = ({ size = 20, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps & { themeColors?: any }) => {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    icon: colors.textMuted,
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => {
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: buttonLayout.y + buttonLayout.height + 4,
          insetInlineStart: buttonLayout.x,
          width: buttonLayout.width,
          zIndex: 99999,
          backgroundColor: dropdownTheme.bg,
          border: `1px solid ${dropdownTheme.border}`,
          borderRadius: tokens.radii.absolute["radius-12"],
          paddingTop: tokens.spacing.absolute["2"],
          paddingBottom: tokens.spacing.absolute["2"],
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
        }}
      >
        {options.map((option) => (
          <div
            key={option.value}
            style={{ paddingTop: tokens.spacing.absolute["2_5"], paddingBottom: tokens.spacing.absolute["2_5"], paddingInlineStart: tokens.spacing.absolute["4"], paddingInlineEnd: tokens.spacing.absolute["4"], cursor: 'pointer', textAlign: getTextAlign(isRTL), direction: 'rtl' }}
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
          >
            <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, width: '100%' }]}>{option.label}</Text>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[styles.dropdownButton, { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border }, disabled && styles.dropdownDisabled]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[styles.dropdownButtonText, { color: dropdownTheme.text }, !selectedOption && { color: dropdownTheme.placeholder }]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// KPI Options
// ============================================================

// KPI Options will be created inside component to use translations
// See getKpiOptions() function inside AddGoalScreen component

// ============================================================
// Main Component
// ============================================================

export default function AddGoalScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();
  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (isViewOnly) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, navigation, navigateToHome]);
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: colors.textPrimary,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    checkboxBorder: isDark ? '#4B5563' : tokens.colors.raw.gray["300"],
    checkboxBg: colors.card,
  };

  const goalId = route.params?.goalId;
  const organizationId = route.params?.organizationId;
  const unitId = route.params?.unitId;
  const scrollToKpi = route.params?.scrollToKpi;
  const isEditMode = !!goalId;
  const kpiSectionRef = useRef<View>(null);

  // Department a new goal is saved under. Single-department users always get
  // their own; a multi-department user must choose one (pre-selected when the
  // screen is opened for a department they manage, e.g. from a unit's page).
  const { isMultiDepartment, departments, departmentId, setDepartmentId } =
    useTargetDepartment(route.params?.organizationId);
  // The focus-driven loader below is created once per goalId, so it reads the
  // current choice through a ref rather than a stale closure.
  const departmentIdRef = useRef(departmentId);
  departmentIdRef.current = departmentId;
  // A goal added from a unit's page is linked to that unit after saving, so it
  // stays in the unit's department: no picker once that department is in place.
  const departmentFixedByUnit = !!unitId && !!organizationId && departmentId === organizationId;
  // Multi-department user creating a goal: everything follows the picker.
  const followsPicker = isMultiDepartment && !isEditMode && !user?.profile?.can_view_all_organizations;

  // KPI Options with translations
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];


  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useUnsavedChangesWarning(!!title || !!description);
  const [weight, setWeight] = useState('30');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [organization, setOrganization] = useState('');
  const [rawOrganizations, setRawOrganizations] = useState<{ id: number; name: string }[]>([]);
  const [orgUsedWeight, setOrgUsedWeight] = useState(0); // Total weight used by other goals in the org

  // Memoized translated organizations - updates when language changes
  const organizations = useMemo(() =>
    rawOrganizations.map((org) => ({
      value: org.id.toString(),
      label: translateOrg(org.name),
    })),
    [rawOrganizations, translateOrg]
  );

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]); // Track KPIs to delete
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);

  // Execution-responsibility units (additive KPI field "مسؤولية التنفيذ").
  // `units` = all org units (fallback so already-saved selections stay labelled);
  // `goalUnitOptions` = units linked to the goal being edited (the dropdown source).
  const [units, setUnits] = useState<{ id: number; name: string; name_ar?: string }[]>([]);
  const [goalUnitOptions, setGoalUnitOptions] = useState<{ value: string; label: string }[]>([]);
  // Guards the focus reload so it skips the extra run caused by clearing the
  // return params after coming back from the Add-Unit screen (see focus effects).
  const returnedFromAddUnitRef = useRef(false);

  const allUnitOptions = useMemo(
    () => units.map(u => ({ value: u.id.toString(), label: translate(u.name_ar || u.name) })),
    [units, translate]
  );

  // Fetch every org unit once (fallback labels for already-saved KPI selections).
  // For a multi-department user creating a goal this list is the KPI
  // responsibility dropdown itself, so it holds only the chosen department's
  // units (none until one is chosen) and reloads when the choice changes; KPI
  // selections that are not in the new department are dropped.
  useEffect(() => {
    let mounted = true;
    if (followsPicker && !departmentId) {
      setUnits([]);
      return;
    }
    (async () => {
      try {
        const res = await organizationalUnitsApi.list({
          page_size: 9999,
          ...(followsPicker ? { department_id: departmentId } : {}),
        });
        if (!mounted) return;
        const loaded = (res.results || []).map(u => ({ id: u.id, name: u.name, name_ar: u.name_ar }));
        setUnits(loaded);
        if (followsPicker) {
          const unitIds = new Set(loaded.map(u => String(u.id)));
          setKpis(prev => prev.map(k => ({ ...k, responsibleUnits: k.responsibleUnits.filter(v => unitIds.has(v)) })));
        }
      } catch {
        /* non-fatal: optional field */
        if (mounted && followsPicker) {
          setUnits([]);
          setKpis(prev => prev.map(k => ({ ...k, responsibleUnits: [] })));
        }
      }
    })();
    return () => { mounted = false; };
  }, [followsPicker, departmentId]);

  // Load the units linked to a specific goal (the dropdown source in edit mode).
  const loadGoalUnits = useCallback(async (gid?: number) => {
    if (!gid) { setGoalUnitOptions([]); return; }
    try {
      const res = await organizationalUnitsApi.list({ strategic_goals: gid, page_size: 9999 });
      setGoalUnitOptions((res.results || []).map(u => ({ value: u.id.toString(), label: translate(u.name_ar || u.name) })));
    } catch {
      setGoalUnitOptions([]);
    }
  }, [translate]);

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Scroll to KPI section when navigated with scrollToKpi
  useEffect(() => {
    if (scrollToKpi && !initialLoading) {
      setKpiSectionExpanded(true);
      setTimeout(() => {
        kpiSectionRef.current?.measureLayout(
          scrollViewRef.current?.getInnerViewNode?.() || (scrollViewRef.current as any)?._innerViewRef,
          (_x: number, y: number) => {
            scrollViewRef.current?.scrollTo({ y: y - 20, animated: true });
          },
          () => {}
        );
      }, 300);
    }
  }, [scrollToKpi, initialLoading]);

  // Load organizations and goal data - use useFocusEffect to reload on every navigation.
  // EXCEPTION: when returning from the Add-Unit screen (route carries createdUnitId),
  // we must NOT reload — that would discard the user's in-progress KPI edits. Instead
  // we merge the newly-created unit into the target KPI and clear the return params.
  useFocusEffect(
    useCallback(() => {
      const currentGoalId = route.params?.goalId;
      const createdUnitId = route.params?.createdUnitId;
      const returnKpiId = route.params?.returnKpiId;

      if (createdUnitId) {
        // Returning from Add-Unit: preserve in-progress edits, auto-select the new unit.
        returnedFromAddUnitRef.current = true; // make the post-clear re-run skip reload
        if (returnKpiId) {
          setKpis(prev => prev.map(k => k.id === returnKpiId
            ? { ...k, responsibleUnits: Array.from(new Set([...k.responsibleUnits, String(createdUnitId)])) }
            : k));
        }
        loadGoalUnits(currentGoalId); // refresh dropdown options (fire and forget)
        navigation.setParams({ createdUnitId: undefined, returnKpiId: undefined });
        return;
      }

      // Clearing the return params above re-fires this effect; skip that single
      // reload so the merged KPI edits survive, then consume the flag.
      if (returnedFromAddUnitRef.current) {
        returnedFromAddUnitRef.current = false;
        return;
      }

      console.log('[AddGoal] Screen focused, goalId:', currentGoalId, 'scrollToKpi:', route.params?.scrollToKpi);
      loadInitialData(currentGoalId);
    }, [route.params?.goalId, route.params?.createdUnitId, route.params?.returnKpiId, loadGoalUnits])
  );

  // Fetch total used weight for an organization's goals (excluding current goal in edit mode)
  const usedWeightRequestRef = useRef(0); // only the latest request may set the value
  const loadOrgUsedWeight = async (orgId: string, excludeGoalId?: number) => {
    const requestId = ++usedWeightRequestRef.current;
    try {
      const goalsResponse = await goalsApi.list({ department_id: orgId, page_size: 200 });
      if (requestId !== usedWeightRequestRef.current) return;
      const goals = goalsResponse.results || [];
      const totalUsed = goals.reduce((sum: number, g: any) => {
        if (excludeGoalId && g.id === excludeGoalId) return sum;
        return sum + (parseFloat(g.weight) || 0);
      }, 0);
      setOrgUsedWeight(Math.round(totalUsed * 100) / 100);
    } catch {
      if (requestId !== usedWeightRequestRef.current) return;
      setOrgUsedWeight(0);
    }
  };

  // Multi-department user creating a goal: the form's department and the
  // weight-usage bar follow the picker (nothing is counted until one is chosen).
  useEffect(() => {
    if (!followsPicker) return;
    setOrganization(departmentId ? departmentId.toString() : '');
    if (departmentId) {
      loadOrgUsedWeight(departmentId.toString());
    } else {
      usedWeightRequestRef.current += 1;
      setOrgUsedWeight(0);
    }
  }, [followsPicker, departmentId]);

  const loadInitialData = async (currentGoalId?: number) => {
    try {
      setInitialLoading(true);

      // Load organizations (for strategy_team users)
      const canViewAll = user?.profile?.can_view_all_organizations;
      let resolvedOrgId = '';
      if (canViewAll) {
        const orgsResponse = await organizationsApi.list();
        setRawOrganizations(
          orgsResponse.results.map((org: any) => ({
            id: org.id,
            name: org.name,
          }))
        );
        // Pre-select organization if provided via route params
        if (organizationId) {
          setOrganization(organizationId.toString());
          resolvedOrgId = organizationId.toString();
        }
      } else if (departmentIdRef.current) {
        setOrganization(departmentIdRef.current.toString());
        resolvedOrgId = departmentIdRef.current.toString();
      }

      // Load goal data if editing
      const editGoalId = currentGoalId || goalId;
      if (editGoalId) {
        // Refresh the goal-linked units list for the KPI responsibility dropdown.
        loadGoalUnits(editGoalId);
        console.log('[AddGoal] Loading goal data for goalId:', editGoalId);
        const goalData = await goalsApi.get(editGoalId);
        console.log('[AddGoal] Goal data loaded:', goalData.title);
        setTitle(goalData.title || '');
        setDescription(goalData.description || '');
        setWeight(goalData.weight || '30');
        setStartDate(goalData.start_date || '');
        setTargetDate(goalData.target_completion_date || '');
        setOrganization(goalData.department_id?.toString() || '');
        resolvedOrgId = goalData.department_id?.toString() || '';

        // Load existing KPIs for this goal using the same endpoint as GoalDetail
        // This returns direct KPIs + KPIs from linked processes + KPIs from linked initiatives
        console.log('[AddGoal] Loading KPIs for goal:', editGoalId);
        const kpisData = await goalsApi.getKPIs(editGoalId);
        console.log('[AddGoal] KPIs loaded:', kpisData?.length, 'KPIs found');
        if (kpisData && kpisData.length > 0) {
          setKpis(kpisData.map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            weight: kpi.weight?.toString() || '30',
            description: kpi.description || '',
            kpiType: 'strategic',
            polarity: kpi.polarity || '',
            unit: kpi.unit || '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            targetValue: kpi.target_value?.toString() || '0',
            worstAcceptableValue: kpi.worst_acceptable_value != null ? kpi.worst_acceptable_value.toString() : '',
            dataSource: kpi.data_source || '',
            goalWeight: '1.0',
            startDate: kpi.measurement_start_date || '',
            target: configFromKpi(kpi),
            isExpanded: false,
            hasReadings: (kpi.readings_count || 0) > 0,
            responsibleUnits: (kpi.responsible_units || []).map((id: number) => String(id)),
          })));
        } else {
          setKpis([]);
        }
      }

      // Load org weight usage
      if (resolvedOrgId) {
        await loadOrgUsedWeight(resolvedOrgId, editGoalId);
      }
    } catch (error: any) {
      showError(t('common.loadingError'));
      console.error('[AddGoal] Error loading data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Generate unique ID for new KPIs
  const generateId = () => `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Add new KPI
  const addKpi = () => {
    const newKpi: KPIFormData = {
      id: generateId(),
      name: '',
      weight: '30',
      description: '',
      kpiType: 'strategic',
      polarity: '',
      unit: '%',
      frequency: 'yearly',
      formula: '',
      targetValue: '0.0',
      worstAcceptableValue: '',
      dataSource: '',
      goalWeight: '1.0',
      startDate: '',
      target: emptyKpiTargetConfig,
      isExpanded: true,
      responsibleUnits: [],
    };
    setKpis([...kpis, newKpi]);
  };

  // Update KPI
  const updateKpi = (id: string, field: keyof KPIFormData, value: any) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, [field]: value } : kpi)));
  };

  // Remove KPI — open confirmation first
  const removeKpi = (id: string) => {
    const target = kpis.find(k => k.id === id);
    const backendId = !id.startsWith('new_') ? parseInt(id, 10) : null;
    setKpiPendingDelete({
      id,
      backendId: backendId !== null && !isNaN(backendId) ? backendId : null,
      name: (target as any)?.name || '',
    });
  };

  const confirmRemoveKpi = () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prevKpis => prevKpis.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expansion
  const toggleKpiExpansion = (id: string) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi)));
  };

  // Validate form
  const validateForm = () => {
    // A multi-department user must say which department a new goal is for.
    if (followsPicker && !departmentId) {
      showError(t('common.selectTargetDepartment'));
      return false;
    }
    if (!title.trim()) {
      showError(t('validation.goalNameRequired'));
      return false;
    }
    if (!description.trim()) {
      showError(t('validation.goalDescriptionRequired'));
      return false;
    }
    if (!startDate) {
      showError(t('validation.startDateRequired'));
      return false;
    }
    if (!targetDate) {
      showError(t('validation.targetDateRequired'));
      return false;
    }
    if (startDate && targetDate && startDate > targetDate) {
      showError(t('validation.startDateBeforeTarget'));
      return false;
    }
    if (!organization && user?.profile?.can_view_all_organizations) {
      showError(t('validation.organizationRequired'));
      return false;
    }
    // Validate KPI required fields
    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) {
      showError(kpiError);
      return false;
    }
    // Validate KPI annual-target config (end date, pattern, step values) —
    // mirrors KPISerializer.validate() so a missing verification/measurement-end
    // date is caught here instead of round-tripping to a 400 masked by success.
    const kpiTargetError = validateKpiTargetConfigs(kpis, t);
    if (kpiTargetError) {
      showError(kpiTargetError);
      return false;
    }
    // Validate KPI measurement start date does not exceed goal's target completion date
    const dateError = validateKpiMeasurementDate(kpis, targetDate);
    if (dateError) {
      showError(dateError);
      return false;
    }
    return true;
  };

  // Save goal
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const goalData: any = {
        title: title.trim(),
        description: description.trim(),
        weight: parseFloat(weight) || 30,
        start_date: startDate,
        target_completion_date: targetDate,
      };

      // Set department_id (backend model field)
      if (user?.profile?.can_view_all_organizations) {
        goalData.department_id = parseInt(organization);
      } else if (departmentId) {
        goalData.department_id = departmentId;
      }

      let savedGoalId: number;

      // Check if user is org_employee (needs to submit for approval)
      const isOrgEmployee = user?.profile?.user_type === 'org_employee';

      if (isEditMode && goalId) {
        await goalsApi.update(goalId, goalData);
        savedGoalId = goalId;
        showSuccess(t('goals.updateSuccess'));
      } else {
        const response = await goalsApi.create(goalData);
        savedGoalId = response.id;

        // Backend perform_create auto-submits as pending for org_employee
        if (isOrgEmployee) {
          showSuccess(t('goals.createAndSubmitSuccess'));
        } else {
          showSuccess(t('goals.createSuccess'));
        }
      }

      // Link goal to organizational unit if created from unit detail
      if (unitId && !isEditMode) {
        try {
          const unitData = await organizationalUnitsApi.get(unitId);
          const existingGoalIds = unitData.strategic_goal_ids || [];
          await organizationalUnitsApi.update(unitId, {
            strategic_goal_ids: [...existingGoalIds, savedGoalId],
          } as any);
        } catch (linkError) {
          console.error('Failed to link goal to unit:', linkError);
        }
      }

      // Delete removed KPIs
      let deletedCount = 0;
      let deleteFailed = 0;

      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
            deletedCount++;
          } catch (deleteError: any) {
            deleteFailed++;
          }
        }
      }

      // Show message about deletions
      if (deletedCount > 0) {
        showSuccess(t('kpis.deletedCount', { count: deletedCount }));
      }
      if (deleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Save KPIs - only create new ones and update modified existing ones
      const kpiErrors: string[] = [];
      // Maps a new KPI's temporary "new_*" id → its real backend id once saved,
      // so retrying after a partial failure UPDATES the saved ones instead of
      // re-creating them (which would 400 on duplicate name).
      const savedIdRemap: Record<string, number> = {};
      for (const kpi of kpis) {
        if (kpi.name.trim()) {
          const kpiData: any = {
            strategic_goal: savedGoalId,
            name: kpi.name.trim(),
            description: kpi.description.trim(),
            kpi_type: kpi.kpiType || 'strategic',
            polarity: kpi.polarity || 'positive',
            frequency: kpi.frequency || 'monthly',
            unit: kpi.unit || '%',
            target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
            weight: parseFloat(kpi.weight) || 30,
            measurement_formula: kpi.formula.trim(),
            data_source: kpi.dataSource.trim(),
            measurement_start_date: kpi.startDate || null,
            source_type: 'strategic_goal',
            responsible_units: kpi.responsibleUnits.map(Number),
          };

          // Check if it's an existing KPI (has numeric id) or new
          if (!kpi.id.startsWith('new_')) {
            try {
              await kpisApi.update(parseInt(kpi.id), {
                ...kpiData,
                ...buildKpiTargetPayload(kpi.target, {
                  unit: kpi.unit,
                  frequency: kpi.frequency,
                  startDate: kpi.startDate,
                }),
              });
            } catch (updateError: any) {
              const errMsg = extractKpiErrorMessage(updateError?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI ${kpi.id} update failed:`, updateError?.response?.data);
            }
          } else {
            try {
              const created = await kpisApi.create({
                ...kpiData,
                ...buildKpiTargetPayload(kpi.target, {
                  unit: kpi.unit,
                  frequency: kpi.frequency,
                  startDate: kpi.startDate,
                }),
              });
              if (created?.id != null) savedIdRemap[kpi.id] = created.id;
            } catch (createError: any) {
              const errMsg = extractKpiErrorMessage(createError?.response?.data);
              kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI create failed:`, createError?.response?.data);
            }
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });

      // A KPI failed to save — surface it and STOP. The goal itself saved, but
      // do NOT navigate away, which previously masked the KPI failure so the
      // user's entered indicators silently "disappeared". Keep them on the form
      // (with saved KPIs promoted to real ids) so they can fix and retry.
      if (kpiErrors.length > 0) {
        if (Object.keys(savedIdRemap).length > 0) {
          setKpis(prev => prev.map(k =>
            savedIdRemap[k.id] != null ? { ...k, id: String(savedIdRemap[k.id]) } : k
          ));
        }
        showError(kpiErrors.join('\n'));
        return;
      }

      if (isEditMode) {
        navigation.goBack();
      } else {
        navigation.navigate('GoalDetail', { id: savedGoalId });
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || t('goals.saveFailed'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { title, description, weight, startDate, targetDate, organization, kpis },
    ready: !initialLoading,
    onExit: handleCancel,
  });

  if (initialLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('goals.strategicGoals'), route: 'Goals' },
            { label: isEditMode ? t('goals.editGoal') : t('goals.addNewGoal') },
          ]}
        />

        {/* Main Card */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
            {isEditMode ? t('goals.editGoal') : t('goals.addNewGoal')}
          </Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            {/* Target department: only for a multi-department user creating a goal.
                An existing goal stays in its own department. */}
            {!isEditMode && !departmentFixedByUnit && (
              <DepartmentPicker departments={departments} value={departmentId} onChange={setDepartmentId} />
            )}

            {/* Name & Weight Row */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('goals.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={weight}
                    onChangeText={(v) => setWeight(normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                {/* Weight Usage Indicator */}
                {(() => {
                  const currentWeight = parseFloat(weight) || 0;
                  const totalWithCurrent = orgUsedWeight + currentWeight;
                  const isOver = totalWithCurrent > 100;
                  const barPercent = Math.min(totalWithCurrent, 100);
                  const barColor = isOver ? tokens.colors.raw.red["500"] : totalWithCurrent > 80 ? tokens.colors.raw.yellow["500"] : tokens.colors.raw.green["500"];
                  return (
                    <View style={styles.weightCapContainer}>
                      <View style={styles.weightCapBar}>
                        <View style={[styles.weightCapFill, { width: `${barPercent}%`, backgroundColor: barColor }]} />
                      </View>
                      <Text style={[styles.weightCapText, { color: isOver ? tokens.colors.raw.red["500"] : themeColors.textMuted }]}>
                        {t('goals.weightUsed')}: {Math.round(totalWithCurrent)}% / 100%
                        {isOver ? ` (${t('goals.weightExceeded')})` : ` (${t('goals.weightRemaining')}: ${Math.round(100 - totalWithCurrent)}%)`}
                      </Text>
                    </View>
                  );
                })()}
              </View>

              {/* Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.name')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={title}
                  onChangeText={setTitle}
                  placeholder={t('goals.goalNamePlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Description Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={description}
                onChangeText={setDescription}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Date Fields Row - RTL: target date on left, start date on right */}
            <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute["3"] }}>
              {/* Target Date Field */}
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.targetDate')}</Text>
                </View>
                <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: tokens.typography.paragraph["paragraph small font size"],
                        fontFamily: 'inherit',
                        color: themeColors.inputText,
                        textAlign: getTextAlign(isRTL),
                        direction: 'rtl',
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={themeColors.iconColor} />
                      <TextInput
                        style={[styles.dateInput, { color: themeColors.inputText }]}
                        value={targetDate}
                        onChangeText={setTargetDate}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={themeColors.placeholder}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
              </View>

              {/* Start Date Field */}
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.startDate')}</Text>
                </View>
                <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: tokens.typography.paragraph["paragraph small font size"],
                        fontFamily: 'inherit',
                        color: themeColors.inputText,
                        textAlign: getTextAlign(isRTL),
                        direction: 'rtl',
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={themeColors.iconColor} />
                      <TextInput
                        style={[styles.dateInput, { color: themeColors.inputText }]}
                        value={startDate}
                        onChangeText={setStartDate}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={themeColors.placeholder}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
              </View>
            </View>
          </View>

          {/* KPIs Section */}
          <View ref={kpiSectionRef} style={[styles.kpiSection, { backgroundColor: themeColors.section }]}>
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.strategicKPIs')}</Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {/* KPI Cards */}
                {kpis.map((kpi, index) => (
                  <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
                    {/* KPI Header */}
                    <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
                      <View style={styles.kpiCardHeaderLeft}>
                        <TouchableOpacity
                          style={styles.kpiHeaderButton}
                          onPress={() => toggleKpiExpansion(kpi.id)}
                        >
                          {kpi.isExpanded ? (
                            <ChevronUpIcon size={20} color={themeColors.iconColor} />
                          ) : (
                            <ChevronDownIcon size={20} color={themeColors.iconColor} />
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.kpiHeaderButton}
                          onPress={() => removeKpi(kpi.id)}
                        >
                          <TrashIcon size={18} color={themeColors.iconColor} />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.kpiCardHeaderRight}>
                        <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
                          {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
                        </Text>
                        <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
                          <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
                        </View>
                        <DragHandleIcon size={20} color={themeColors.iconColor} />
                      </View>
                    </View>

                    {/* KPI Content - Expanded */}
                    {kpi.isExpanded && (
                      <View style={styles.kpiCardContent}>
                        {/* Basic Information Subsection */}
                        <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

                          {/* Name & Weight Row */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            {/* Weight */}
                            <View style={styles.fieldSmall}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                              </View>
                              <View style={styles.weightInputRow}>
                                <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                                  <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                                </TouchableOpacity>
                                <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                                <TextInput
                                  style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                  value={kpi.weight}
                                  onChangeText={(v) => updateKpi(kpi.id, 'weight', normalizeArabicNumbers(v))}
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                            </View>

                            {/* Name */}
                            <View style={styles.fieldLarge}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.name}
                                onChangeText={(v) => updateKpi(kpi.id, 'name', v)}
                                placeholder=""
                                textAlign="right"
                              />
                            </View>
                          </View>

                          {/* Description */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.description')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={kpi.description}
                              onChangeText={(v) => updateKpi(kpi.id, 'description', v)}
                              placeholder=""
                              textAlign="right"
                              multiline
                              numberOfLines={3}
                            />
                          </View>
                        </View>

                        {/* Classification Subsection */}
                        <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.classification')}</Text>

                          <View style={[styles.row, isMobile && styles.rowMobile, { zIndex: 10 }]}>
                            {/* Polarity */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.polarity}
                                options={polarityOptions}
                                placeholder={t('kpis.selectPolarityHint')}
                                onChange={(v) => updateKpi(kpi.id, 'polarity', v)}
                              />
                            </View>

                            {/* KPI Type */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                              </View>
                              <Dropdown
                                value={'strategic'}
                                options={kpiTypeOptions}
                                placeholder={t('kpis.selectClassification')}
                                onChange={(v) => updateKpi(kpi.id, 'kpiType', v)}
                                disabled={true}
                              />
                            </View>
                          </View>

                        </View>

                        {/* Implementation Responsibility Units (additive, optional).
                            Edit mode (existing goalId): dropdown limited to goal-linked
                            units + an "add unit" CTA. New unsaved goal: all org units,
                            no CTA (cannot link a unit to a goal that has no id yet). */}
                        <View style={{ marginTop: tokens.spacing.absolute["3"], marginBottom: tokens.spacing.absolute["4"], zIndex: 5 }}>
                          <Text style={[styles.label, { color: themeColors.label, marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
                          <MultiSelectDropdown
                            values={kpi.responsibleUnits}
                            options={(() => {
                              const base = goalId ? [...goalUnitOptions] : [...allUnitOptions];
                              // Never drop an already-saved selection that isn't in the base list.
                              kpi.responsibleUnits.forEach(v => {
                                if (!base.find(o => o.value === v)) {
                                  const found = allUnitOptions.find(o => o.value === v);
                                  if (found) base.push(found);
                                }
                              });
                              return base;
                            })()}
                            placeholder={t('initiatives.selectResponsibleUnits')}
                            emptyActionLabel={goalId ? t('common.addUnit') : undefined}
                            onEmptyAction={goalId ? () => navigation.navigate('AddOrganizationalUnit', {
                              preselectGoalId: goalId,
                              returnTo: 'AddGoal',
                              returnKpiId: kpi.id,
                            }) : undefined}
                            onChange={(vals) => updateKpi(kpi.id, 'responsibleUnits', vals)}
                          />
                        </View>

                        {/* Measurement + annual target grid — shared section for new AND existing KPIs */}
                        <KpiAnnualTargetSection
                          config={kpi.target}
                          onChange={(patch) => updateKpi(kpi.id, 'target', { ...kpi.target, ...patch })}
                          startDate={kpi.startDate}
                          unit={kpi.unit}
                          frequency={kpi.frequency}
                          formula={kpi.formula}
                          dataSource={kpi.dataSource}
                          onMeasurementChange={(field, value) => updateKpi(kpi.id, field, value)}
                        />
                      </View>
                    )}
                  </View>
                ))}

                {/* Add KPI Button */}
                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={addKpi}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addPerformanceIndicator')}</Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <Button
            variant="primary"
            onPress={handleSave}
            loading={saving}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="secondary"
            onPress={requestCancel}
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKpi}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles (matching Figma design 2448-138681)
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    // Padding set dynamically via inline style for responsiveness
    paddingBottom: tokens.spacing.absolute["12"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
    flexWrap: 'wrap',
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Main Card
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Field styles
  field: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  fieldSmall: {
    width: '30%',
    minWidth: 100,
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  rowMobile: {
    flexDirection: 'column',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.red["500"],
  },
  // Input styles
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    minHeight: 44,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  weightInput: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    width: 60,
    textAlign: 'center',
  },
  percentSymbol: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  setWeightButton: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  setWeightButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  weightCapContainer: {
    marginTop: tokens.spacing.absolute["1_5"],
  },
  weightCapBar: {
    height: 6,
    backgroundColor: tokens.colors.raw.gray["200"],
    borderRadius: 3,
    overflow: 'hidden',
  },
  weightCapFill: {
    height: '100%',
    borderRadius: 3,
  },
  weightCapText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    marginTop: 3,
    textAlign: getTextAlign(isRTL),
  },
  // Percent Input
  percentInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  percentInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  percentLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2"],
  },
  dateInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
  },
  dropdownDisabled: {
    opacity: 0.6,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownPlaceholder: {
    color: tokens.colors.raw.gray["400"],
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    minHeight: 44,
    justifyContent: 'center',
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // KPI Section
  kpiSection: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
    minHeight: 44,
  },
  kpiSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  kpiSectionContent: {
    padding: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["0"],
  },
  // KPI Card
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardNumber: {
    width: 28,
    height: 28,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.slate["100"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiCardNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiCardName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiCardContent: {
    padding: tokens.spacing.absolute["4"],
  },
  // Subsection
  subsection: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  subsectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderStyle: 'dashed',
    backgroundColor: tokens.colors.raw.white,
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["6"],
  },
});

const styles = getStyles(true);
